import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Minus, AlertCircle, Camera, Eye, 
  Activity, Zap, BarChart3, RefreshCw, Heart, Brain, Wind, Moon, Footprints, HeartHandshake
} from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

// Body silhouette component using gender-specific images
const BodySilhouette = ({ highlights = {}, isScanning = false, gender = 'unknown', visceralFat }) => {
  const isFemale = gender?.toLowerCase() === 'female';
  const twinImage = isFemale ? '/twin-female.svg' : '/twin-male.svg';

  const getVFColor = (val) => {
    if (val == null) return '#6b7280';
    if (val <= 12) return '#8BB89F';
    if (val <= 18) return '#F8B64C';
    return '#D54B48';
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {isScanning && (
        <motion.div 
          className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60 z-10"
          initial={{ top: '10%' }}
          animate={{ top: ['10%', '90%', '10%'] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />
      )}
      
      <motion.div 
        className="relative w-full h-full flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <img 
          src={twinImage}
          alt="Digital Twin"
          className="w-full h-full max-w-[140px] max-h-[260px] object-contain"
          style={{ 
            filter: isScanning ? 'brightness(1.1) drop-shadow(0 0 15px rgba(0, 200, 255, 0.4))' : 'drop-shadow(0 0 10px rgba(0, 200, 255, 0.2))'
          }}
        />
        
        {isScanning && (
          <motion.div 
            className="absolute inset-0 bg-cyan-400/10 rounded-full"
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        )}

        {/* Visceral Fat overlay — positioned at abdomen */}
        {visceralFat != null && (
          <motion.div
            className="absolute top-[48%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            data-testid="visceral-fat-overlay"
          >
            <div
              className="px-2 py-1 rounded-lg border backdrop-blur-sm text-center"
              style={{
                backgroundColor: `${getVFColor(visceralFat)}15`,
                borderColor: `${getVFColor(visceralFat)}40`,
              }}
            >
              <p className="text-[9px] uppercase tracking-wider opacity-70" style={{ color: getVFColor(visceralFat) }}>VF</p>
              <p className="text-sm font-bold leading-none" style={{ color: getVFColor(visceralFat) }}>{visceralFat}%</p>
            </div>
          </motion.div>
        )}
      </motion.div>
      
      <motion.div 
        className="absolute top-[15%] right-[10%] w-2 h-2 rounded-full bg-cyan-400"
        animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <motion.div 
        className="absolute top-[40%] left-[5%] w-1.5 h-1.5 rounded-full bg-primary"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 2.5, repeat: Infinity, delay: 0.3 }}
      />
      <motion.div 
        className="absolute bottom-[30%] right-[5%] w-1.5 h-1.5 rounded-full bg-[#8BB89F]"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1.8, repeat: Infinity, delay: 0.6 }}
      />
    </div>
  );
};

// Segmented Ring Component
const SegmentedRing = ({ segments, size = 200, score }) => {
  const radius = (size - 30) / 2;
  const circumference = 2 * Math.PI * radius;
  const segmentCount = segments.length;
  const gap = 4; // Gap between segments in degrees
  const segmentAngle = (360 - (gap * segmentCount)) / segmentCount;
  
  return (
    <svg width={size} height={size} className="absolute inset-0">
      {segments.map((segment, index) => {
        const startAngle = index * (segmentAngle + gap) - 90;
        const endAngle = startAngle + segmentAngle * (segment.value / 100);
        const bgEndAngle = startAngle + segmentAngle;
        
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;
        const bgEndRad = (bgEndAngle * Math.PI) / 180;
        
        const x1 = size/2 + radius * Math.cos(startRad);
        const y1 = size/2 + radius * Math.sin(startRad);
        const x2 = size/2 + radius * Math.cos(endRad);
        const y2 = size/2 + radius * Math.sin(endRad);
        const bgX2 = size/2 + radius * Math.cos(bgEndRad);
        const bgY2 = size/2 + radius * Math.sin(bgEndRad);
        
        const largeArc = segmentAngle > 180 ? 1 : 0;
        const progressArc = (endAngle - startAngle) > 180 ? 1 : 0;
        
        return (
          <g key={segment.name}>
            {/* Background arc */}
            <path
              d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${bgX2} ${bgY2}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              className="text-muted/20"
              strokeLinecap="round"
            />
            {/* Progress arc */}
            <motion.path
              d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${progressArc} 1 ${x2} ${y2}`}
              fill="none"
              stroke={segment.color}
              strokeWidth="8"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, delay: index * 0.2, ease: 'easeOut' }}
              style={{ filter: `drop-shadow(0 0 6px ${segment.color}40)` }}
            />
          </g>
        );
      })}
    </svg>
  );
};

// Mini Metric Badge
const MiniMetric = ({ icon: Icon, label, value, unit, status }) => {
  const statusColors = {
    good: 'text-[#8BB89F] bg-[#8BB89F]/10 border-[#8BB89F]/20',
    warning: 'text-[#F8B64C] bg-[#F8B64C]/10 border-[#F8B64C]/20',
    critical: 'text-[#D54B48] bg-[#D54B48]/10 border-[#D54B48]/20',
  };
  
  const colors = statusColors[status] || statusColors.good;
  
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border ${colors} transition-all hover:scale-105`}>
      <Icon size={12} className="opacity-80" />
      <span className="text-xs font-semibold">{value}</span>
      <span className="text-[10px] opacity-70">{unit}</span>
    </div>
  );
};

// Sparkline Component
const Sparkline = ({ data, width = 100, height = 30, color = '#8BB89F' }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="opacity-80">
      <defs>
        <linearGradient id="sparklineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Area fill */}
      <polygon
        points={`0,${height} ${points} ${width},${height}`}
        fill="url(#sparklineGradient)"
      />
      {/* Line */}
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Current point */}
      <circle
        cx={width}
        cy={height - ((data[data.length - 1] - min) / range) * (height - 4) - 2}
        r="3"
        fill={color}
      />
    </svg>
  );
};

// Quick Action Button
const QuickAction = ({ icon: Icon, label, onClick, variant = 'default' }) => {
  const variants = {
    default: 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground',
    primary: 'bg-primary/10 hover:bg-primary/20 text-primary',
    warning: 'bg-[#F8B64C]/10 hover:bg-[#F8B64C]/20 text-[#F8B64C]',
  };
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105 ${variants[variant]}`}
    >
      <Icon size={12} />
      {label}
    </button>
  );
};

// Main Advanced Digital Twin Dashboard Component
export function AdvancedDigitalTwin({
  score = 0,
  trajectory = 'steady',
  confidence = 'high',
  topDrivers = [],
  breakdown = {},
  biometrics = {},
  trendData = [],
  gender = 'unknown',
  visceralFat,
  onViewTrends,
  onRescan,
  onFixData,
  onViewDrivers,
  onConnectCare,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  // Determine body highlights based on drivers and biometrics
  const getBodyHighlights = () => {
    const highlights = { heart: 'good', brain: 'good', lungs: 'good' };
    
    if (topDrivers.includes('Elevated heart rate') || topDrivers.includes('Low HRV')) {
      highlights.heart = 'warning';
    }
    if (topDrivers.includes('Sleep debt') || (biometrics.sleep_hours && biometrics.sleep_hours < 6)) {
      highlights.brain = 'warning';
    }
    if (topDrivers.includes('Low activity') || (biometrics.steps && biometrics.steps < 5000)) {
      highlights.lungs = 'warning';
    }
    
    return highlights;
  };
  
  // Segment data for the ring
  const segments = [
    { name: 'Biometrics', value: breakdown?.biometrics || 75, color: '#00C8FF' },
    { name: 'Behavior', value: breakdown?.behavior || 70, color: '#8BB89F' },
    { name: 'Sleep', value: breakdown?.sleep || 65, color: '#9B87F5' },
    { name: 'Mood', value: breakdown?.mood || 80, color: '#F8B64C' },
    { name: 'Activity', value: breakdown?.activity || 60, color: '#C10891' },
  ];
  
  // Get score color
  const getScoreColor = () => {
    if (score >= 80) return '#8BB89F';
    if (score >= 60) return '#F8B64C';
    return '#D54B48';
  };
  
  // Get mini metrics status
  const getMetricStatus = (type, value) => {
    switch(type) {
      case 'hrv':
        return value >= 50 ? 'good' : value >= 35 ? 'warning' : 'critical';
      case 'sleep':
        return value >= 7 ? 'good' : value >= 5 ? 'warning' : 'critical';
      case 'steps':
        return value >= 8000 ? 'good' : value >= 5000 ? 'warning' : 'critical';
      default:
        return 'good';
    }
  };
  
  const isLowConfidence = confidence?.toLowerCase() === 'low';
  const TrajectoryIcon = trajectory === 'up' ? TrendingUp : trajectory === 'down' ? TrendingDown : Minus;
  const trajectoryColor = trajectory === 'up' ? '#8BB89F' : trajectory === 'down' ? '#D54B48' : '#F8B64C';
  
  return (
    <TooltipProvider>
      <motion.div 
        className="relative bg-gradient-to-br from-card via-card to-muted/20 rounded-3xl p-6 border border-border overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="advanced-digital-twin"
      >
        {/* Background animated grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(0,200,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,0.3) 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }} />
        </div>
        
        {/* Ambient glow */}
        <motion.div 
          className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-20"
          style={{ background: `radial-gradient(circle, ${getScoreColor()}, transparent)` }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        
        {/* Main content grid */}
        <div className="relative grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
          {/* Left Column - Body Avatar */}
          <div className="flex flex-col items-center">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Digital Twin</p>
            <div className="relative w-32 h-48">
              <BodySilhouette 
                highlights={getBodyHighlights()} 
                isScanning={isLowConfidence}
                gender={gender}
                visceralFat={visceralFat}
              />
            </div>
            
            {/* Body system indicators */}
            <div className="flex gap-2 mt-2">
              <Tooltip>
                <TooltipTrigger>
                  <div className={`p-1.5 rounded-full ${getBodyHighlights().heart === 'good' ? 'bg-[#8BB89F]/20 text-[#8BB89F]' : 'bg-[#F8B64C]/20 text-[#F8B64C]'}`}>
                    <Heart size={12} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>Cardiovascular: {getBodyHighlights().heart}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger>
                  <div className={`p-1.5 rounded-full ${getBodyHighlights().brain === 'good' ? 'bg-[#8BB89F]/20 text-[#8BB89F]' : 'bg-[#F8B64C]/20 text-[#F8B64C]'}`}>
                    <Brain size={12} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>Neural/Sleep: {getBodyHighlights().brain}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger>
                  <div className={`p-1.5 rounded-full ${getBodyHighlights().lungs === 'good' ? 'bg-[#8BB89F]/20 text-[#8BB89F]' : 'bg-[#F8B64C]/20 text-[#F8B64C]'}`}>
                    <Wind size={12} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>Respiratory/Activity: {getBodyHighlights().lungs}</TooltipContent>
              </Tooltip>
            </div>
          </div>
          
          {/* Center Column - Score Ring */}
          <div className="relative flex flex-col items-center">
            <div className="relative" style={{ width: 200, height: 200 }}>
              {/* Segmented outer ring */}
              <SegmentedRing segments={segments} size={200} score={score} />
              
              {/* Pulse animation for low confidence */}
              {isLowConfidence && (
                <motion.div
                  className="absolute inset-2 rounded-full border-2 border-[#F8B64C]"
                  animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.7, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
              
              {/* Inner score display */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <motion.span
                  className="text-5xl font-bold font-heading"
                  style={{ color: getScoreColor() }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: 'spring' }}
                >
                  {score}
                </motion.span>
                
                {/* Trajectory */}
                <div className="flex items-center gap-1 mt-1">
                  <TrajectoryIcon size={14} style={{ color: trajectoryColor }} />
                  <span className="text-xs" style={{ color: trajectoryColor }}>
                    {trajectory === 'up' ? '+3 this week' : trajectory === 'down' ? '-2 this week' : 'Stable'}
                  </span>
                </div>
                
                {/* Confidence */}
                <div className={`flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-[10px] ${isLowConfidence ? 'bg-[#F8B64C]/10 text-[#F8B64C]' : 'bg-muted text-muted-foreground'}`}>
                  {isLowConfidence && <AlertCircle size={10} />}
                  Confidence: {confidence}
                </div>
              </div>
            </div>
            
            {/* Segment legend */}
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-3 max-w-[200px]">
              {segments.map((seg) => (
                <Tooltip key={seg.name}>
                  <TooltipTrigger>
                    <div className="flex items-center gap-1 cursor-help">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: seg.color }} />
                      <span className="text-[9px] text-muted-foreground">{seg.name}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{seg.name}: {seg.value}%</TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
          
          {/* Right Column - Metrics & Trend */}
          <div className="flex flex-col items-center gap-3">
            {/* Mini metrics */}
            <div className="flex flex-col gap-2">
              <MiniMetric 
                icon={Activity} 
                label="HRV" 
                value={biometrics?.hrv || 48} 
                unit="ms"
                status={getMetricStatus('hrv', biometrics?.hrv || 48)}
              />
              <MiniMetric 
                icon={Moon} 
                label="Sleep" 
                value={biometrics?.sleep_hours?.toFixed(1) || '6.3'} 
                unit="hrs"
                status={getMetricStatus('sleep', biometrics?.sleep_hours || 6.3)}
              />
              <MiniMetric 
                icon={Footprints} 
                label="Steps" 
                value={(biometrics?.steps || 5420).toLocaleString()} 
                unit=""
                status={getMetricStatus('steps', biometrics?.steps || 5420)}
              />
            </div>
            
            {/* Trend sparkline */}
            <div className="mt-2">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1 text-center">7-Day Trend</p>
              <Sparkline 
                data={trendData.length > 0 ? trendData : [65, 68, 72, 70, 75, 73, score]}
                width={80}
                height={30}
                color={getScoreColor()}
              />
            </div>
          </div>
        </div>
        
        {/* Top Drivers */}
        {topDrivers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/50">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Key Factors</p>
            <div className="flex flex-wrap gap-2">
              {topDrivers.map((driver, i) => (
                <span
                  key={i}
                  className="px-2 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-medium"
                >
                  {driver}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Quick Actions */}
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <QuickAction icon={Eye} label="View Drivers" onClick={onViewDrivers} />
          <QuickAction icon={BarChart3} label="Weekly Trends" onClick={onViewTrends} variant="primary" />
          <QuickAction icon={HeartHandshake} label="Connect to Care" onClick={onConnectCare} />
          {isLowConfidence && (
            <QuickAction icon={AlertCircle} label="Fix Data" onClick={onFixData} variant="warning" />
          )}
        </div>
        
        {/* Status message */}
        <motion.p 
          className="text-center text-xs text-muted-foreground mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          {score >= 80 
            ? "Your body is in great alignment. Keep it up!" 
            : score >= 60 
              ? `Your body is stable${topDrivers.length > 0 ? `, but ${topDrivers[0].toLowerCase()}` : '.'}`
              : "Some areas need attention. Check the drivers above."}
        </motion.p>
      </motion.div>
    </TooltipProvider>
  );
}

export default AdvancedDigitalTwin;
